import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { DXFEntity, DXFLayerConfig } from './types';

interface DXFPreview2DProps {
  entities: DXFEntity[];
  layerConfig: Record<string, DXFLayerConfig>;
}

export const DXFPreview2D: React.FC<DXFPreview2DProps> = ({ entities, layerConfig }) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [zoom, setZoom] = useState(1);
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const isPanning = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    const w = container.clientWidth;
    const h = container.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    canvas.style.width = w + 'px';
    canvas.style.height = h + 'px';
    ctx.scale(dpr, dpr);

    ctx.fillStyle = '#0f1117';
    ctx.fillRect(0, 0, w, h);

    const active = entities.filter((e) => layerConfig[e.layer]?.enabled);
    if (active.length === 0) {
      ctx.fillStyle = '#4b5563';
      ctx.font = '13px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(t('projects.newProject.dxf.noActiveLayers'), w / 2, h / 2);
      return;
    }

    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;
    active.forEach((e) => {
      e.vertices.forEach(([x, y]) => {
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      });
    });

    const pad = 40;
    const scaleX = (w - pad * 2) / (maxX - minX || 1);
    const scaleY = (h - pad * 2) / (maxY - minY || 1);
    const baseScale = Math.min(scaleX, scaleY);
    const scale = baseScale * zoom;

    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const tx = (x: number) => (x - cx) * scale + w / 2 + panOffset.x;
    const ty = (y: number) => -(y - cy) * scale + h / 2 + panOffset.y;

    active.forEach((entity) => {
      const cfg = layerConfig[entity.layer];
      ctx.strokeStyle = cfg?.color || '#666';
      ctx.lineWidth = 1.2;
      ctx.globalAlpha = 0.8;
      ctx.beginPath();
      entity.vertices.forEach(([x, y], i) => {
        if (i === 0) ctx.moveTo(tx(x), ty(y));
        else ctx.lineTo(tx(x), ty(y));
      });
      if (entity.closed) ctx.closePath();
      ctx.stroke();

      if (entity.closed && entity.vertices.length >= 3) {
        ctx.fillStyle = cfg?.color || '#666';
        ctx.globalAlpha = 0.08;
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    });
  }, [entities, layerConfig, zoom, panOffset, t]);

  useEffect(() => {
    draw();
  }, [draw]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const observer = new ResizeObserver(() => draw());
    observer.observe(container);
    return () => observer.disconnect();
  }, [draw]);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    setZoom((prev) => Math.max(0.1, Math.min(20, prev * (1 - e.deltaY * 0.001))));
  }, []);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) {
      isPanning.current = true;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    }
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setPanOffset((prev) => ({ x: prev.x + dx, y: prev.y + dy }));
  }, []);

  const handleMouseUp = useCallback(() => {
    isPanning.current = false;
  }, []);

  return (
    <div
      ref={containerRef}
      className="w-full h-full relative cursor-grab active:cursor-grabbing"
      onWheel={handleWheel}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <canvas ref={canvasRef} className="block" />
      <div className="absolute top-3 left-3 text-xs text-gray-500 font-mono">
        {t('projects.newProject.dxf.preview2D')}
      </div>
      <div className="absolute bottom-3 right-3 text-[10px] text-gray-600 font-mono">{zoom.toFixed(1)}x</div>
    </div>
  );
};
